import {Children, ClassComponent, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LinkButton from 'flarum/common/components/LinkButton';
import Dropdown from 'flarum/common/components/Dropdown';
import avatar from 'flarum/common/helpers/avatar';
import userOnline from 'flarum/common/helpers/userOnline';
import username from 'flarum/common/helpers/username';
import listItems from 'flarum/common/helpers/listItems';
import humanTime from 'flarum/common/helpers/humanTime';
import EditablePictureAttribute from '../components/EditablePictureAttribute';
import PictureList from '../components/PictureList';
import PictureListState from '../states/PictureListState';
import EditablePictureLicense from '../components/EditablePictureLicense';
import Picture from '../models/Picture';
import {cimaiseAlbum, cimaisePicture} from '../routes';
import ExifSummary from './ExifSummary';
import ExifGeoLocation from './ExifGeoLocation';
import PictureControls from '../utils/PictureControls';
import PictureViewBox from './PictureViewBox';

const translationPrefix = 'kilowhat-cimaise.forum.picture-show.';

type ScrollDirection = 1 | -1;

interface PictureShowAttrs {
    id: string
}

export default class PictureShow implements ClassComponent<PictureShowAttrs> {
    pictureId!: string
    picture?: Picture
    keydown!: (event: KeyboardEvent) => void

    oninit(vnode: Vnode<PictureShowAttrs>) {
        this.pictureId = vnode.attrs.id;

        const preloadedPicture = app.preloadedApiDocument() as Picture;

        if (preloadedPicture) {
            this.picture = preloadedPicture;
            this.pictureReady();
        } else {
            this.picture = app.store.getById('cimaise-pictures', this.pictureId);

            if (this.picture) {
                this.pictureReady();
            } else {
                app.store.find<Picture>('cimaise/pictures', this.pictureId).then(picture => {
                    this.picture = picture;
                    this.pictureReady();
                    m.redraw();
                });
            }
        }
    }

    oncreate(vnode: Vnode<PictureShowAttrs>) {
        this.keydown = event => {
            if (event.key === 'ArrowLeft') {
                this.changePicture(-1);
            }

            if (event.key === 'ArrowRight') {
                this.changePicture(1);
            }
        };

        document.addEventListener('keydown', this.keydown);
    }

    onremove() {
        document.removeEventListener('keydown', this.keydown);
    }

    pictureReady() {
        const album = this.picture!.album();
        const query = 'album:' + (album && album.id());

        // Create new picture list, but keep existing one if it's for the same album (browsing from album to picture)
        // But could be different if we click on the picture from somewhere else in the app
        if (!app.cache.cimaiseAlbumPictures || app.cache.cimaiseAlbumPictures.params.q !== query) {
            app.cache.cimaiseAlbumPictures = new PictureListState({
                q: query,
            });
            app.cache.cimaiseAlbumPictures.refresh();
        }
    }

    changePicture(direction: ScrollDirection) {
        const index = app.cache.cimaiseAlbumPictures.pictures.indexOf(this.picture!);

        let nextPictureIndex = null;

        const lastIndexOfList = app.cache.cimaiseAlbumPictures.pictures.length - 1;

        if (index === -1) {
            if (direction === 1 && app.cache.cimaiseAlbumPictures.moreResults) {
                app.cache.cimaiseAlbumPictures.loadMore();
            } else if (app.cache.cimaiseAlbumPictures.pictures.length) {
                nextPictureIndex = 0;
            }
        } else if (direction === -1) {
            if (index > 0) {
                nextPictureIndex = index - 1;
            } else {
                // Loop to the end
                //TODO: check if the list can be loaded further?
                nextPictureIndex = lastIndexOfList;
            }
        } else if (direction === 1) {
            if (index === lastIndexOfList) {
                // Loop back to start
                nextPictureIndex = 0;
            } else if (index < lastIndexOfList) {
                nextPictureIndex = index + 1;
            }

            // If we are near the end of the list, try to load more
            if ((lastIndexOfList - index) <= 2 && app.cache.cimaiseAlbumPictures.moreResults) {
                app.cache.cimaiseAlbumPictures.loadMore();
            }
        }

        if (nextPictureIndex !== null) {
            m.route.set(cimaisePicture(app.cache.cimaiseAlbumPictures.pictures[nextPictureIndex]));
        }
    }

    view() {
        const picture = this.picture;

        if (!picture) {
            return m('.container', m('p', app.translator.trans(translationPrefix + 'loading')));
        }

        const album = picture.album();

        // Should never happen, but necessary for typescript
        if (!album) {
            return null;
        }

        const user = picture.user() || null;

        const controls = PictureControls.controls(picture, {
            ondelete() {
                m.route.set(cimaiseAlbum(album));
            },
        }).toArray();

        return m('.PictureShow', [
            m('.PictureView', [
                m('.PictureView-nav', [
                    LinkButton.component({
                        className: 'Button',
                        icon: 'fas fa-chevron-left',
                        href: cimaiseAlbum(album),
                    }, app.translator.trans(translationPrefix + 'back-album')),
                    m('.PictureView-nav-flex'),
                    Dropdown.component({
                        className: 'AlbumThumbnail-controls',
                        menuClassName: 'Dropdown-menu--right',
                        buttonClassName: 'Button',
                        label: app.translator.trans(translationPrefix + 'download'),
                        icon: 'fas fa-download',
                    }, Object.keys(picture.imageUrls()).map((key: string) => {
                        const info = picture.imageUrls()[key];

                        let label: Children = key;

                        if (info.length === 3) {
                            label = app.translator.trans(translationPrefix + 'download-cropped', {
                                size: info[1] + 'x' + info[2]
                            })
                        } else if (key === 'original') {
                            label = app.translator.trans(translationPrefix + 'download-original');
                        }

                        return m('a', {
                            href: info[0],
                        }, label);
                    })),
                ]),
                m(PictureViewBox, {
                    picture,
                }),
                m('.PictureView-directions', [
                    Button.component({
                        className: 'Button Button--icon',
                        icon: 'fas fa-chevron-left',
                        onclick: () => {
                            this.changePicture(-1);
                        },
                    }, app.translator.trans(translationPrefix + 'previous')),
                    Button.component({
                        className: 'Button Button--icon',
                        icon: 'fas fa-chevron-right',
                        onclick: () => {
                            this.changePicture(1);
                        },
                    }, app.translator.trans(translationPrefix + 'next')),
                ]),
                m('.PictureView-browser', m(PictureList, {
                    state: app.cache.cimaiseAlbumPictures,
                    thumbnailAttrs: {
                        showUser: true,
                    },
                })),
            ]),
            m('.PictureDetails', m('.container', [
                m('.PictureDetails--row', [
                    m('.PictureDetails--column', [
                        m('.App-titleControl', picture.title()),
                        m('.PictureDetails--user', [
                            controls.length ? Dropdown.component({
                                className: 'App-primaryControl PictureDetails-controls',
                                menuClassName: 'Dropdown-menu--right',
                                buttonClassName: 'Button Button--icon Button--flat',
                                label: app.translator.trans('kilowhat-cimaise.forum.picture-controls.controls'),
                                icon: 'fas fa-ellipsis-v',
                            }, controls) : null,
                            m('.PostUser', [
                                m('h3', m(Link, {
                                    href: user ? app.route.user(user) : '/',
                                }, [
                                    avatar(user, {
                                        className: 'PostUser-avatar'
                                    }),
                                    user ? userOnline(user) : null,
                                    username(user),
                                ])),
                                user ? m('ul.PostUser-badges badges', listItems(user.badges().toArray())) : null,
                            ]),
                            picture.createdAt() ? m('.PictureDetails--time', app.translator.trans(translationPrefix + 'published-at', {
                                ago: humanTime(picture.createdAt() as any),
                            })) : null,
                            picture.takenAt() ? m('.PictureDetails--time', app.translator.trans(translationPrefix + 'taken-at', {
                                ago: humanTime(picture.takenAt() as any),
                            })) : null,
                        ]),
                        m('h2', m(EditablePictureAttribute, {
                            picture,
                            attribute: 'title',
                            editable: picture.canRename(),
                        })),
                        picture.canRename() || picture.description() ? m(EditablePictureAttribute, {
                            picture,
                            attribute: 'description',
                            editable: picture.canRename(),
                            inputElement: 'textarea',
                        }) : null,
                    ]),
                    m('.PictureDetails--column', [
                        m(ExifSummary, {
                            picture,
                        }),
                        m(ExifGeoLocation, {
                            picture,
                        }),
                        m('.CimaiseLicenseSection', [
                            m('.CimaiseSectionTitle', m('h3', 'License')),
                            m(EditablePictureLicense, {
                                picture,
                            }),
                            picture.canLicense() || picture.author() ? m(EditablePictureAttribute, {
                                picture,
                                attribute: 'author',
                                editable: picture.canLicense(),
                            }) : null,
                        ]),
                    ]),
                ]),
            ])),
        ]);
    }
}
